import {
  Box,
  FieldSwitch,
  FieldText,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  TabsWithContent,
} from '@dixa/lib-shared-habla';
import type {
  Template,
  TemplateNormalized,
  TemplatesUpdate,
  TemplateTranslations,
  TemplateTranslationsNormalized,
  TemplateTypes,
} from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useMemo } from 'react';
import { Field, FormErrors, InjectedFormProps, reduxForm } from 'redux-form';

import { FieldTemplate } from 'components/fieldTemplate';
import { withTemplates, WithTemplatesInjectedProps } from 'hocs';
import { TemplatesResponse } from 'hocs/templates/api/types';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { validateName } from 'utils/formValidator';

import styles from './templatesModal.module.scss';
import {
  defaultLanguageCode,
  getDenormalizedTranslations,
  helpText,
  languages,
} from './utils';

const TEMPLATE_NAMES: Record<TemplateTypes, string> = {
  quick_response: 'Quick response',
  email_auto_reply: 'Auto reply',
  email_signature: 'Email signature',
};

type FormValues = {
  name: string;
  subject?: string;
  content: string;
  translations: TemplateTranslationsNormalized;
};

type OwnProps = {
  confirmName?: string;
  currentTemplate?: TemplateNormalized;
  initialValues?: TemplateNormalized;
  isOpen: boolean;
  modalTitle?: string;
  shouldNotifyOnCreate?: boolean;
  onClose: VoidFunction;
  onCreate?: (template: Template) => void;
  type: TemplateTypes;
};

type Props = WithTemplatesInjectedProps &
  OwnProps &
  InjectedFormProps<FormValues, OwnProps, FormErrors>;

type Errors = {
  content?: string;
  name?: string;
  translations?: Record<string, string>;
};

const validate = (values: FormValues): Errors => {
  const errors: Errors = {};

  if (!values) {
    return errors;
  }

  if (!values.content?.trim()) {
    errors.content = 'Required';
  }

  if (values.translations) {
    Object.keys(values.translations).forEach((locale) => {
      const translation = values.translations[locale];

      if (
        !translation.content &&
        (!!translation.subject || translation.isEnabled)
      ) {
        if (errors.translations) {
          errors.translations[locale] = 'Required';
        } else {
          errors.translations = { [locale]: 'Required' };
        }
      }
    });
  }

  return errors;
};

const TemplatesModalImpl = ({
  confirmName,
  currentTemplate,
  handleSubmit,
  invalid,
  isOpen,
  modalTitle,
  onCreate,
  onClose,
  shouldNotifyOnCreate = true,
  templates,
  type,
}: Props): JSX.Element => {
  const notify = useNotifications();

  const createNewTemplate = useCallback(
    ({ name, content, subject, translations }: FormValues) => {
      templates
        .create({
          name,
          subject,
          content,
          type,
        })
        .then((response: TemplatesResponse) => {
          const updateTranslationsPromise =
            Object.keys(translations || {}).length === 0
              ? Promise.resolve() //  if there is no translation, do not call the endpoint
              : templates.updateTranslations(
                  response.data.id,
                  getDenormalizedTranslations(translations),
                );

          updateTranslationsPromise.then(() => {
            if (shouldNotifyOnCreate) {
              notify(
                {
                  title: `Template created`,
                  text: `Template "${name}" created`,
                },
                NotificationType.Success,
              );
            }

            onCreate?.(response.data);
            onClose();
          });
        })
        .catch((err) => {
          const hasConflict = err.response?.status === 409;

          notify(
            {
              title: hasConflict ? 'Duplicate name' : 'An error occurred',
              text: hasConflict
                ? `Template with name "${name}" already exists`
                : `An error occurred when trying to create the "${name}" template`,
            },
            NotificationType.Error,
          );
        });
    },
    [shouldNotifyOnCreate, notify, onClose, onCreate, templates, type],
  );

  const getTranslationsUpdatesAndDeletes = useCallback(
    (
      translations: TemplateTranslationsNormalized = {},
    ): {
      updates: TemplateTranslations;
      deletes: string[];
    } => {
      const updates = getDenormalizedTranslations(translations);
      const deletes = Object.keys(currentTemplate?.translations || {}).reduce<
        string[]
      >((dels, locale) => (updates[locale] ? dels : dels.concat([locale])), []);

      return { updates, deletes };
    },
    [currentTemplate?.translations],
  );

  const updateTemplate = useCallback(
    ({ name, content, subject, translations }: FormValues) => {
      const payload: TemplatesUpdate = {
        updates: {
          name: name.trim(),
          content,
          ...(subject && { subject }),
        },
        deletes: currentTemplate?.subject && !subject ? ['subject'] : [],
      };

      templates
        .update(currentTemplate?.id as string, payload)
        .then(({ data: { id: templateId } }: TemplatesResponse) => {
          const { deletes, updates } =
            getTranslationsUpdatesAndDeletes(translations);

          templates.updateTranslations(templateId, updates).then(() => {
            const deletePromise = deletes.length
              ? templates.removeTranslations(templateId, deletes)
              : Promise.resolve();

            deletePromise.then(() => {
              notify(
                {
                  title: `Template updated`,
                  text: `Template "${name}" updated`,
                },
                NotificationType.Success,
              );

              onClose();
            });
          });
        })
        .catch(() => {
          notify(
            {
              title: 'An error occurred',
              text: `An error occurred when trying to update the "${name}" template`,
            },
            NotificationType.Error,
          );
        });
    },
    [
      currentTemplate,
      getTranslationsUpdatesAndDeletes,
      notify,
      onClose,
      templates,
    ],
  );

  const handleFormSubmit = useCallback(
    (values: FormValues) => {
      const errors = validate(values);

      if (errors.translations) {
        Object.keys(errors.translations).forEach((locale) => {
          const language = languages.find(
            (language) => language.code === locale,
          );
          const text = values.translations[locale].isEnabled
            ? `Please enter the content for "${language?.label}" or disable the selection`
            : `Please enter the content for "${language?.label}" or remove the subject`;

          notify(
            {
              title: `Invalid "${language?.label}" content`,
              text,
            },
            NotificationType.Error,
          );
        });

        return;
      }

      ampli.settingsTemplatesModalFormSubmitted({
        mode: currentTemplate ? 'edit' : 'add',
        templateType: type,
        templateName: currentTemplate?.name ?? values.name,
      });

      if (!currentTemplate) {
        createNewTemplate(values);
      } else {
        updateTemplate(values);
      }
    },
    [createNewTemplate, currentTemplate, notify, type, updateTemplate],
  );

  const tabsLabels = useMemo(
    () =>
      languages.map((lang) => ({
        id: lang.code,
        label: (
          <>
            <span className={styles.tabLanguageName}>{lang.label}</span>
            {lang.code === defaultLanguageCode ? (
              <span className={styles.tabLanguageDefault}>Default</span>
            ) : (
              <Field
                id={`switch-${lang.code}`}
                name={`translations[${lang.code}]['isEnabled']`}
                component={FieldSwitch}
                type="checkbox"
              />
            )}
          </>
        ),
      })),
    [],
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <ModalHead
          title={
            modalTitle ||
            (currentTemplate
              ? `Edit ${TEMPLATE_NAMES[type].toLowerCase()} "${
                  currentTemplate?.name
                }"`
              : `Add new ${TEMPLATE_NAMES[type].toLowerCase()}`)
          }
          onClose={onClose}
        />
        <ModalBody className={styles.templatesModalBody}>
          <Field
            component={FieldText}
            helpIcon={{
              title: 'Template name',
              text: 'Template name should only be in lowercase and not contain any whitespace or special characters',
            }}
            label="Template name"
            name="name"
            placeholder="Template name..."
            required
            validate={validateName}
          />

          <Box padding="400 0 0 0">
            <h5 className={styles.translationsHeader}>Template Translations</h5>
            <Box className={styles.translationsBody}>
              <label className={styles.languagesLabel}>Languages</label>
              <TabsWithContent
                tabs={tabsLabels}
                initiallySelectedTabId={defaultLanguageCode}
                tabsPlacement="left"
                tabsClassName={styles.tabs}
                tabClassName={styles.tab}
                tabSelectedClassName={styles.tabSelected}
              >
                {languages.map(({ code }) => {
                  const isDefaultLanguageCode = code === defaultLanguageCode;

                  return (
                    <div className={styles.languageForm} key={code}>
                      <Field
                        label="Subject"
                        name={
                          isDefaultLanguageCode
                            ? 'subject'
                            : `translations[${code}]['subject']`
                        }
                        component={FieldText}
                        placeholder="Subject..."
                        helpIcon={{
                          title: 'Subject',
                          text: `This field is used to fill out the subject line in emails when composing emails and for auto-replies. The subject line is not used if the template is used in chat or you are replying to an email. You can use these variables: ${helpText}`,
                        }}
                      />
                      <div className={styles.templateContentContainer}>
                        <Field
                          label="Template content"
                          displayName="Template Content"
                          name={
                            isDefaultLanguageCode
                              ? 'content'
                              : `translations[${code}]['content']`
                          }
                          component={FieldTemplate}
                          height="355px" // value is found by trial and error
                          required
                          placeholder="Content..."
                          showHelpIcon
                          helpIcon={{
                            title: 'Template Content',
                            text: `The content is written in HTML and supports [Liquid](https://shopify.github.io/liquid/) too. \n\n${helpText}`,
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </TabsWithContent>
            </Box>
          </Box>
        </ModalBody>
        <ModalFoot
          cancelOnClick={onClose}
          confirmOnClick={handleSubmit(handleFormSubmit)}
          confirmName={
            confirmName ||
            `${currentTemplate ? 'Save' : 'Add'} ${TEMPLATE_NAMES[
              type
            ].toLowerCase()} template`
          }
          confirmProps={{ type: 'submit', disabled: invalid }}
        />
      </form>
    </Modal>
  );
};

export const TemplatesModal = reduxForm<FormValues, OwnProps, FormErrors>({
  form: 'TemplatesModalForm',
  enableReinitialize: true,
  validate,
})(withTemplates(TemplatesModalImpl));
